import React, { useId, useMemo } from 'react';
import {
  FINANCING_KEY,
  deadlineKey,
  getContingencyOptions,
  getUndeterminedContingencyLabels,
  groupContingencyOptions,
  isVacantLandProperty,
  isWisconsinHomeOffer,
  withoutFinancingTerms,
} from '../../contingencyCatalog';
import ContingencyDeadlineField from '../../ContingencyDeadlineField';
import { ContingencyFieldError } from '../../ContingencyElectionErrors';
import CureElectionField from '../../CureElectionField';
import FinancingTermsField from '../../FinancingTermsField';
import TitlePeriodsField from '../../TitlePeriodsField';
import LandContingencyNote, { ContingencyDeadlineNote } from '../../LandContingencyNote';
import LeadPaintElectionField from '../../LeadPaintElectionField';
import { dateOnlyInDays as dateInDays } from '../../utils';
import {
  dateInDaysInZone,
  deadlineTimeLabel,
  getExpirationBounds,
  getExpirationError,
  isShortDeadline,
} from '../../offerExpiration';
import OfferDeadlineDisclaimer from '../../OfferDeadlineDisclaimer';
import './TermsStep.css';

const CLOSING_PRESETS = [30, 45, 60];
const EXPIRATION_PRESETS = [1, 3, 7];

/**
 * @param {Object} props
 * @param {Object|null} props.serverErrors - the server's contingency field
 *   errors (`{inspection: msg, soil_evaluation_days: msg}`) from a 422.
 */
export default function TermsStep({ wiz, property, serverErrors = null }) {
  const closingId = useId();
  const expiresId = useId();
  const expiresHintId = useId();
  const additionalId = useId();
  const contingencies = wiz.form.contingencies || {};

  const groups = useMemo(
    () => groupContingencyOptions(getContingencyOptions(property)),
    [property]
  );
  // Well/septic are hidden when the seller left the matching utility field
  // blank. Say which one, rather than leaving the buyer to wonder where the
  // option went (T-39). Tracked per field: a listing can state its water
  // source and omit sewer, and the buyer still needs to know about septic.
  const undetermined = useMemo(
    () => getUndeterminedContingencyLabels(property),
    [property]
  );
  const isLand = isVacantLandProperty(property);
  // WI residential and condo: every selected contingency needs a deadline
  // (user decision), said once above the list.
  const isWiHome = isWisconsinHomeOffer(property);

  // Switching a contingency off drops its deadline with it (and financing its loan terms).
  const toggle = (key, on) => {
    let next = { ...contingencies, [key]: on };
    if (!on) delete next[deadlineKey(key)];
    if (!on && key === FINANCING_KEY) next = withoutFinancingTerms(next);
    wiz.setField('contingencies', next);
  };

  const timeZone = property?.timezone;
  const expiresBounds = getExpirationBounds(wiz.form.closing_date, timeZone);
  const expirationError = getExpirationError(wiz.form.expires_on, wiz.form.closing_date, timeZone);

  return (
    <section className="terms-step">
      <header className="terms-step__intro">
        <h3>Set your terms</h3>
        <p>Pick a closing date, how long your offer stands, and the contingencies that protect you.</p>
      </header>

      <div className="terms-step__field">
        <label htmlFor={closingId} className="terms-step__label">Closing date</label>
        <div className="terms-step__chips">
          {CLOSING_PRESETS.map((days) => {
            const date = dateInDays(days);
            const active = wiz.form.closing_date === date;
            return (
              <button
                key={days}
                type="button"
                className={`terms-step__chip${active ? ' terms-step__chip--active' : ''}`}
                onClick={() => wiz.setField('closing_date', date)}
              >
                {days} days
              </button>
            );
          })}
        </div>
        <input
          id={closingId}
          type="date"
          className="terms-step__date"
          value={wiz.form.closing_date}
          onChange={(e) => wiz.setField('closing_date', e.target.value)}
          required
        />
      </div>

      <div className="terms-step__field">
        <label htmlFor={expiresId} className="terms-step__label">Offer valid until</label>
        <div className="terms-step__chips">
          {EXPIRATION_PRESETS.map((days) => {
            const date = dateInDaysInZone(days, timeZone);
            const active = wiz.form.expires_on === date;
            return (
              <button
                key={days}
                type="button"
                className={`terms-step__chip${active ? ' terms-step__chip--active' : ''}`}
                onClick={() => wiz.setField('expires_on', date)}
              >
                {days === 1 ? '1 day' : `${days} days`}
              </button>
            );
          })}
        </div>
        <input
          id={expiresId}
          type="date"
          className="terms-step__date"
          value={wiz.form.expires_on || ''}
          min={expiresBounds.min}
          max={expiresBounds.max}
          onChange={(e) => wiz.setField('expires_on', e.target.value)}
          aria-describedby={expiresHintId}
          aria-invalid={!!expirationError}
          required
        />
        {expirationError ? (
          <p id={expiresHintId} className="terms-step__error" role="alert">{expirationError}</p>
        ) : (
          <p id={expiresHintId} className="terms-step__hint">
            The seller must accept or counter in cutthe6.com by{' '}
            <strong>{deadlineTimeLabel(wiz.form.expires_on, timeZone)}</strong> on this date,
            the property&apos;s local time. If they don&apos;t, your offer expires
            automatically. You can still withdraw your offer at any time before the
            seller accepts it.
          </p>
        )}
        {!expirationError && isShortDeadline(wiz.form.expires_on, timeZone) && (
          <p className="terms-step__warning">
            Heads up: the seller will have less than a day to respond.
          </p>
        )}
        <OfferDeadlineDisclaimer />
      </div>

      <fieldset className="terms-step__contingencies">
        <legend className="terms-step__legend">Contingencies</legend>
        <p className="terms-step__hint terms-step__hint--block">
          None are added by default. Pick the ones you want — each lets you
          withdraw without losing your earnest money if its condition is not met.
        </p>
        {isLand && (
          <p className="terms-step__hint terms-step__hint--block" data-testid="terms-land-hint">
            This is a vacant land listing, so the offer uses land contingencies.
          </p>
        )}
        {/* WI.offer.contingency_deadline.note (legal copy file §10). */}
        {isWiHome && <ContingencyDeadlineNote />}

        {groups.map((group) => (
          <div key={group.key} className="terms-step__group">
            <h4 className="terms-step__group-title">{group.label}</h4>
            {/* WI.offer.land_contingency_note (legal L4 §9.7): deadlines are required. */}
            {group.key === 'land' && <LandContingencyNote />}
            <div className="terms-step__cards">
              {group.options.map(({ key, title, label, description, deadline, deadlineRequired }) => {
                const checked = !!contingencies[key];
                const descId = `contingency-${key}-desc`;
                return (
                  <div key={key} className="terms-step__card-wrap">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={checked}
                    aria-describedby={descId}
                    className={`terms-step__card${checked ? ' terms-step__card--on' : ''}`}
                    onClick={() => toggle(key, !checked)}
                  >
                    <div className="terms-step__card-body">
                      <div className="terms-step__card-header">
                        <span className="terms-step__card-title">{title}</span>
                        <span
                          className={`terms-step__toggle${checked ? ' terms-step__toggle--on' : ''}`}
                          aria-hidden="true"
                        >
                          <span className="terms-step__toggle-dot" />
                        </span>
                      </div>
                      <p id={descId} className="terms-step__card-desc">
                        {description}
                      </p>
                    </div>
                  </button>
                  <ContingencyFieldError field={key} message={serverErrors?.[key]} />
                  {checked && deadline && (
                    <ContingencyDeadlineField
                      contingencyKey={key}
                      label={label}
                      value={contingencies[deadlineKey(key)]}
                      required={deadlineRequired}
                      serverError={serverErrors?.[deadlineKey(key)]}
                      onChange={(text) =>
                        wiz.setField('contingencies', { ...contingencies, [deadlineKey(key)]: text })
                      }
                    />
                  )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* WI offer with financing: the loan the contingency is about (QA N13). */}
        <FinancingTermsField
          category={wiz.termsCategory}
          contingencies={contingencies}
          onChange={(next) => wiz.setField('contingencies', next)}
          amount={wiz.derived.loanAmount}
          downPaymentPercent={wiz.form.down_payment_percent}
          serverErrors={serverErrors}
        />

        {/* Pre-1978 or unknown-year WI home: inspect or waive, required. */}
        <LeadPaintElectionField
          mode={wiz.leadPaintMode}
          contingencies={contingencies}
          onChange={(next) => wiz.setField('contingencies', next)}
          serverErrors={serverErrors}
        />

        {/* WI offer with a curable contingency: the 7(d) cure election, required. */}
        <CureElectionField
          category={wiz.cureCategory}
          contingencies={contingencies}
          onChange={(next) => wiz.setField('contingencies', next)}
          serverErrors={serverErrors}
        />

        {/* Vacant land has no existing well or septic system to inspect; the
            soil evaluation and water supply contingencies cover it. */}
        {undetermined.length > 0 && !isLand && (
          <p className="terms-step__hint terms-step__hint--block">
            This listing does not say whether{' '}
            <strong>{undetermined.join(' or ')}</strong>{' '}
            {undetermined.length === 1 ? 'applies' : 'apply'}, so{' '}
            {undetermined.length === 1 ? 'that contingency is' : 'those contingencies are'}{' '}
            not offered. Ask the seller to confirm, or add the request under
            Additional terms below.
          </p>
        )}
      </fieldset>

      {/* Every WI offer: when the title commitment is due and how long you have to object (QA N13). */}
      <TitlePeriodsField
        category={wiz.termsCategory}
        contingencies={contingencies}
        onChange={(next) => wiz.setField('contingencies', next)}
        closingDate={wiz.form.closing_date}
        timeZone={timeZone}
        serverErrors={serverErrors}
      />

      <div className="terms-step__field">
        <label htmlFor={additionalId} className="terms-step__label">
          Additional terms <span className="terms-step__optional">optional</span>
        </label>
        <textarea
          id={additionalId}
          className="terms-step__textarea"
          rows="3"
          value={wiz.form.additional_terms}
          onChange={(e) => wiz.setField('additional_terms', e.target.value)}
          placeholder="e.g. Seller covers home warranty, leave appliances, etc."
        />
        <p className="terms-step__hint">Anything else the seller should know about your offer.</p>
      </div>
    </section>
  );
}
